import { Component, computed, effect, HostListener } from '@angular/core'
import {
  ModalButtonLabel,
  ModalResponse,
} from '@evologi/shared/data-access-api'
import { NgModalService } from '@evologi/admin/ui'
import { ButtonColor } from '@evologi/admin/ui'

export interface ModalButton {
  label: ModalButtonLabel
  response: ModalResponse
}

@Component({
  selector: 'op-ng-dialog',
  templateUrl: './ng-dialog.component.html',
  styleUrls: ['./ng-dialog.component.scss'],
})
export class NgDialogComponent {
  visible = false

  constructor(private modalService: NgModalService) {
    // auto-show/hide dialog when config changes
    effect(() => {
      const shouldBeVisible = !!this.modalService.dialogConfig()
      if (!shouldBeVisible && this.visible) {
        // If config is cleared but dialog is still visible, ensure it closes
        this.visible = false
      } else {
        this.visible = shouldBeVisible
      }
    })
  }

  @HostListener('document:keydown', ['$event'])
  onEscapeKey(event: KeyboardEvent) {
    if (
      event.key === 'Escape' &&
      this.visible &&
      this.modalService.dialogConfig()
    ) {
      event.preventDefault()
      event.stopPropagation()
      this.modalService.respond('closed')
    }
  }

  title = computed(() => this.modalService.dialogConfig()?.title ?? 'Attention')
  titleParams = computed(() => this.modalService.dialogConfig()?.titleParams)
  message = computed(() => this.modalService.dialogConfig()?.message ?? '')
  messageParams = computed(
    () => this.modalService.dialogConfig()?.messageParams,
  )
  buttons = computed(() =>
    (this.modalService.dialogConfig()?.buttons ?? ['Ok']).map(
      (b): ModalButton => ({
        label: b,
        response: this.buttonResponse(b),
      }),
    ),
  )

  buttonResponse(label: ModalButtonLabel): ModalResponse {
    switch (label) {
      case 'Cancel':
        return 'cancel'
      case 'Open':
        return 'alternative'
      default:
        return 'success'
    }
  }

  buttonColor(label: ModalButtonLabel): ButtonColor {
    switch (label) {
      case 'Save':
      case 'Confirm':
      case 'Continue':
        return 'success'
      case 'Cancel':
        return 'danger'
      default:
        return 'info'
    }
  }

  onButtonClick(response: ModalResponse) {
    this.modalService.respond(response)
  }

  onDialogHide() {
    // Handle dialog close via ESC or outside click
    // Only respond if modal config still exists (wasn't already closed by button click)
    const config = this.modalService.dialogConfig()
    if (config) {
      // Close the dialog properly by clearing the config
      this.modalService.respond('cancel')
    }
    // Ensure visible is false
    this.visible = false
  }
}
